import React, { useRef, useEffect, useState, useContext } from "react"
import { LocaleContext } from "contexts"
import { motion, useInView } from "framer-motion"
import { StaticImage } from "gatsby-plugin-image"
import useTranslations from "hooks/useTranslations"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import SEO from "components/seo"
import Hero from "components/organisms/webinar/Hero"
import Button from "components/buttons/Button"
import { Container } from "components/atoms/materials/Container"
import { NavBar } from "components/organisms/materials/NavBar"
import { HowItWorks2 } from "components/organisms/HowItWorks2"
import Divided from "components/organisms/common/Divided"
import List from "components/List"
import SectionContainer from "components/molecules/common/SectionContainer"
import DisclosureList from "components/organisms/common/DisclosureList"
import HowDoWeHelp from "components/variants/HowDoWeHelpSchoolsOnboarding"
import Quote from "components/organisms/schools/Quote"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"
import LogoGrid from "components/organisms/LogoGrid"
import { ListItem } from "components/organisms/common/NumberedListAnimated"
import OnboardingSection from "components/molecules/onboarding"
import { siteCountry } from "../../utils/env"
import countries from "../../../config/i18n"

const HeroButton = ({ theme = "black" }) => {
  const { locale } = useContext(LocaleContext)
  const { buttonLoginHelp } = useTranslations()
  return (
    <Button
      bgStyle={theme}
      label={buttonLoginHelp}
      link={countries[siteCountry][locale].appLink}
      outbound
      className="animate-scale"
    />
  )
}

const Question = () => (
  <StaticImage
    objectFit="cover"
    objectPosition="left center"
    src="../../images/schools/AdobeStock_183480624.jpeg"
    alt=""
    style={{ display: "contents", minHeight: "2em" }}
  />
)

const HelpOld = ({ title }) => {
  const {
    schoolOnboardingFaq1Question,
    schoolOnboardingFaq1Answer,
    schoolOnboardingFaq2Question,
    schoolOnboardingFaq2Answer1,
    schoolOnboardingFaq2Answer2,
    schoolOnboardingFaq2AnswerLinkUrl,
    schoolOnboardingFaq2AnswerLinkText,
    schoolOnboardingFaq3Question,
    schoolOnboardingFaq3Answer,
    schoolOnboardingFaq4Question,
    schoolOnboardingFaq4Answer1,
    schoolOnboardingFaq4Answer2,
    schoolOnboardingFaq4Answer3,
    schoolOnboardingFaq4Answer4,
    schoolOnboardingFaq4Answer3LinkUrl,
    schoolOnboardingFaq4Answer3LinkText,
    schoolOnboardingFaq5Question,
    schoolOnboardingFaq5Answer,
  } = useTranslations()
  const list = [
    {
      name: schoolOnboardingFaq1Question,
      description: <p className="mb-0 text-sm">{schoolOnboardingFaq1Answer}</p>,
    },
    {
      name: schoolOnboardingFaq2Question,
      description: (
        <div className="flex flex-col space-y-2">
          <p className="mb-0 text-sm">
            {schoolOnboardingFaq2Answer1}
            <a href={schoolOnboardingFaq2AnswerLinkUrl}>
              {schoolOnboardingFaq2AnswerLinkText}
            </a>
            {schoolOnboardingFaq2Answer2}
          </p>
        </div>
      ),
    },
    {
      name: schoolOnboardingFaq3Question,
      description: (
        <div className="flex flex-col space-y-2">
          <p className="mb-0 text-sm">{schoolOnboardingFaq3Answer}</p>
        </div>
      ),
    },
    {
      name: schoolOnboardingFaq4Question,
      description: (
        <div className="flex flex-col space-y-2">
          <p className="mb-0 text-sm">{schoolOnboardingFaq4Answer1}</p>
          <p className="mb-0 text-sm">{schoolOnboardingFaq4Answer2}</p>
          <p className="mb-0 text-sm">
            {schoolOnboardingFaq4Answer3}
            <a
              href={schoolOnboardingFaq4Answer3LinkUrl}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {schoolOnboardingFaq4Answer3LinkText}
            </a>
            {schoolOnboardingFaq4Answer4}
          </p>
        </div>
      ),
    },
    {
      name: schoolOnboardingFaq5Question,
      description: <p className="mb-0 text-sm">{schoolOnboardingFaq5Answer}</p>,
    },
  ]
  return (
    <DisclosureList
      title={title}
      list={list}
      Image={Question}
      initialActive={0}
      reversed
      theme="light"
      buttonStyle="dark"
    />
  )
}

const BoyBullyingImage = () => (
  <StaticImage
    objectFit="cover"
    objectPosition="center"
    src="../../images/schools/AdobeStock_268905554.jpeg"
    alt=""
    style={{ display: "contents" }}
  />
)

const Situations = () => {
  const {
    schoolOnboardingSection2Title,
    schoolOnboardingSituationsDescription1,
    schoolOnboardingSituationsList1,
    schoolOnboardingSituationsList2,
    schoolOnboardingSituationsList3,
    schoolOnboardingSituationsList4,
    schoolOnboardingSituationsList5,
    schoolOnboardingSituationsList6,
    schoolOnboardingSituationsList7,
    schoolOnboardingSituationsList8,
    schoolOnboardingSituationsList9,
    schoolOnboardingSituationsDescription2,
  } = useTranslations()
  return (
    <Divided
      Image={BoyBullyingImage}
      theme="red"
      reversed
      title={schoolOnboardingSection2Title}
      animateTitle
    >
      <>
        <ScrollAnimate delay={0.6} className="mb-0">
          <p className="text-lg text-zinc-800">
            {schoolOnboardingSituationsDescription1}
          </p>
        </ScrollAnimate>
        <ScrollAnimate delay={1} className="mb-0">
          <div className="relative lg:order-1">
            <List
              items={[
                { title: schoolOnboardingSituationsList3, id: 3 },
                { title: schoolOnboardingSituationsList1, id: 1 },
                { title: schoolOnboardingSituationsList2, id: 2 },
                { title: schoolOnboardingSituationsList4, id: 4 },
                { title: schoolOnboardingSituationsList5, id: 5 },
                { title: schoolOnboardingSituationsList6, id: 6 },
                { title: schoolOnboardingSituationsList7, id: 7 },
                { title: schoolOnboardingSituationsList8, id: 8 },
                { title: schoolOnboardingSituationsList9, id: 9 },
              ]}
              bullet={<CheckBadgeIcon className="w-6 text-kkm-red" />}
              textStyle="dark"
            />
          </div>
        </ScrollAnimate>
        <p className="mb-0 text-lg">{schoolOnboardingSituationsDescription2}</p>
      </>
    </Divided>
  )
}

const ImageHowItWorks1 = () => (
  <StaticImage
    src="../../images/howitworks/AdobeStock_296075267.jpg"
    alt=""
    objectFit="cover"
    className="isolate aspect-square overflow-hidden rounded-2xl lg:w-full"
    width={630}
  />
)

const ImageHowItWorks2 = () => (
  <StaticImage
    src="../../images/howitworks/AdobeStock_296076537.jpg"
    alt=""
    objectFit="cover"
    className="isolate aspect-square overflow-hidden rounded-2xl lg:w-full"
    width={630}
  />
)

const ImageHowItWorks3 = () => (
  <StaticImage
    src="../../images/howitworks/AdobeStock_449949703.jpg"
    alt=""
    objectFit="cover"
    className="isolate aspect-square overflow-hidden rounded-2xl lg:w-full"
    width={630}
  />
)

const ImageHowItWorks4 = () => (
  <StaticImage
    src="../../images/howitworks/AdobeStock_296075843.jpg"
    alt=""
    objectFit="cover"
    className="isolate aspect-square overflow-hidden rounded-2xl lg:w-full"
    width={630}
  />
)

const HowItWorksTranslations = () => {
  const images = {
    Image1: ImageHowItWorks1,
    Image2: ImageHowItWorks2,
    Image3: ImageHowItWorks3,
    Image4: ImageHowItWorks4,
  }
  const {
    buttonLoginHelp,
    schoolOnboardingSection1Title,
    schoolOnboardingFeature1Title,
    schoolOnboardingFeature1Description,
    schoolOnboardingFeature2Title,
    schoolOnboardingFeature2Description,
    schoolOnboardingFeature3Title,
    schoolOnboardingFeature3Description,
    schoolOnboardingFeature4Title,
    schoolOnboardingFeature4Description,
  } = useTranslations()
  return (
    <div className="relative">
      <HowItWorks2
        titleAlign="center"
        translations={{
          title: schoolOnboardingSection1Title,
          feature1Title: schoolOnboardingFeature1Title,
          feature1Text: schoolOnboardingFeature1Description,
          feature2Title: schoolOnboardingFeature2Title,
          feature2Text: schoolOnboardingFeature2Description,
          feature3Title: schoolOnboardingFeature3Title,
          feature3Text: schoolOnboardingFeature3Description,
          feature4Title: schoolOnboardingFeature4Title,
          feature4Text: schoolOnboardingFeature4Description,
          buttonLogin: buttonLoginHelp,
        }}
        images={images}
        theme="dark"
      />
    </div>
  )
}

const Section = ({ id, title, description, Content }: { id: string; title: string; description?: string | false; Content: React.ComponentType<SectionProps> }) => {
  return (
    <motion.section
      layout
      id={id}
      aria-labelledby={title}
      className="scroll-mt-14 sm:scroll-mt-14 lg:scroll-mt-24"
    >
      <Content title={title} description={description} />
    </motion.section>
  )
}

const KaisaImage = () => (
  <StaticImage src="../../images/team/kaisaRissanen.png" alt="Photo of Kaisa" />
)

const MaijaImage = () => (
  <StaticImage src="../../images/team/maijaKomonen.png" alt="Photo of Maija" />
)

const Quotes = () => {
  const {
    schoolOnboardingHelpQuote1,
    schoolOnboardingHelpQuote2,
    schoolOnboardingSection4Title,
  } = useTranslations()
  return (
    <SectionContainer
      title={schoolOnboardingSection4Title}
      // className="pb-0 lg:pb-0"
    >
      <div className="flex flex-col pt-10">
        <Quote Image={KaisaImage} text={schoolOnboardingHelpQuote1} reversed />
        <div className="my-10" />
        <Quote Image={MaijaImage} text={schoolOnboardingHelpQuote2} />
      </div>
    </SectionContainer>
  )
}

// The cards sit 2 per row (3 from `xs`) on a grid of twice as many columns,
// each spanning two, so a short last row can be offset to sit centred instead
// of hugging the left edge. Class names stay literal for Tailwind's scanner.
const lastRowCentering = (index: number, count: number) => {
  const classes = ["col-span-2"]
  const isLast = index === count - 1
  if (isLast && count % 2 === 1) classes.push("col-start-2")
  if (count % 3 === 2 && index === count - 2) classes.push("xs:col-start-2")
  else if (count % 3 === 1 && isLast) classes.push("xs:col-start-3")
  else if (isLast && count % 2 === 1) classes.push("xs:col-start-auto")
  return classes.join(" ")
}

const Contents = ({ sections }: { sections: Section[] }) => {
  const [showSectionBoxes, setShowSectionBoxes] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref)
  useEffect(() => {
    if (isInView) setShowSectionBoxes(true)
  }, [isInView])

  const variants = {
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        staggerChildren: 0.5,
        delayChildren: 1.5,
      },
    },
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.3,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  }

  return (
    <div className="relative space-y-10 bg-gray-50 py-16 lg:py-24">
      <Container size="xs" className="flex flex-col space-y-2">
        <div className="flex flex-col items-start space-y-6 md:flex-row md:space-x-10 md:space-y-0">
          <motion.ul
            variants={variants}
            className="m-0 mx-auto grid w-full list-none grid-cols-4 gap-6 xs:grid-cols-6 lg:w-auto"
            animate={showSectionBoxes ? "open" : "hidden"}
            initial="hidden"
            ref={ref}
            layout
          >
            {Object.values(sections).map((s, index) => (
              <ScrollAnimate
                delay={index * 0.4 + 0.4}
                className={lastRowCentering(index, sections.length)}
              >
                <a href={`#${s.id}`}>
                  <ListItem
                    {...s}
                    number={index + 1}
                    theme="light"
                    textClass="text-xl sm:p-2 xs:text-sm xs:p-0 xs:leading-4 sm:text-lg sm:leading-5 md:text-xl md:leading-6 lg:text-xl lg:leading-6 xl:text-xl xl:leading-6"
                  />
                </a>
              </ScrollAnimate>
            ))}
          </motion.ul>
        </div>
      </Container>
    </div>
  )
}

type SectionProps = {
  title: string
  description?: string | false
}

type Section = {
  id: string
  title: string
  content: React.ComponentType<SectionProps>
}

const SchoolOnboarding = ({ pageContext: { currentPage } }) => {
  const {
    pageProOnboardingTitle,
    schoolOnboardingHeroTitle,
    schoolOnboardingHeroDescription,
    schoolOnboardingVideoTitle,
    schoolOnboardingVideoLink,
    schoolOnboardingSection1Title,
    schoolOnboardingSection2Title,
    schoolOnboardingSection3Title,
    schoolOnboardingSection4Title,
    schoolOnboardingSection5Title,
    schoolOnboardingVideoDescription1,
    schoolOnboardingVideoDescription2,
  } = useTranslations()

  const sections: Section[] = [
    {
      id: "palvelu",
      title: schoolOnboardingSection1Title,
      content: HowItWorksTranslations,
    },
    {
      id: "situations",
      title: schoolOnboardingSection2Title,
      content: Situations,
    },
    {
      id: "help",
      title: schoolOnboardingSection4Title,
      content: Quotes,
    },
    {
      id: "howto",
      title: schoolOnboardingSection3Title,
      content: HowDoWeHelp,
    },
    {
      id: "faq",
      title: schoolOnboardingSection5Title,
      content: HelpOld,
    },
  ]

  return (
    <>
      <SEO title={pageProOnboardingTitle} />
      <div className="sections">
        <Hero
          descriptionBelow
          title={schoolOnboardingHeroTitle}
          description={schoolOnboardingHeroDescription}
          HeroButton={HeroButton}
          bgColor="red"
          video="/schools-hero-video.mp4"
        />
        <OnboardingSection
          id="introvideo"
          title={schoolOnboardingVideoTitle}
          descriptions={[
            schoolOnboardingVideoDescription1,
            schoolOnboardingVideoDescription2,
          ]}
          videoLink={schoolOnboardingVideoLink}
        />
        <Contents sections={sections} />
        <NavBar sections={sections} theme="red" />
        {sections.map((section) => (
          <Section
            id={section.id}
            title={section.title}
            description={false}
            Content={section.content}
          />
        ))}
        <LogoGrid
          center
          currentPage={currentPage}
          className="bg-zinc-200"
          cellClassName="bg-white"
        />
      </div>
    </>
  )
}

export default SchoolOnboarding

